/**
 * @module RuleStore
 */
/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import Utils from '../utils/utils';
import { LocaleUtils } from '../locale/locale-utils';
import ParagraphUtils from '../dynamic-content/util';
import { C11nEnv } from '../interpreter/c11n-env';
import AnnotationProcessor from '../annotation/processor';
import '../annotation/handlers';
import type { DataPageObj } from '../globals';
import { type LocalizationService, getLocaleReference } from '../locale/LocalizationService';
import { LocaleRuleTypes } from '../types';

import type {
  PropertyModel,
  AIAgentCoachModel,
  ViewStore,
  DataPage,
  ParagraphStore,
  DataPageModel,
  InsightStore,
  Resources,
  StoreType,
  ResourceType,
  NavigationStore,
  DataType,
  Field,
  PersonalizationStore
} from './types';

const paragraphStore: { [key: string]: ParagraphStore } = {};
const viewStore: { [key: string]: ViewStore } = {};
const navigationStore: { [key: string]: NavigationStore } = {};
const propertyModelStore: { [key: string]: PropertyModel | PropertyModel[] } = {};
const dataPageModelStore: { [key: string]: DataPageModel } = {};
const personalizationStore: { [key: string]: PersonalizationStore } = {};
const insightStore: { [key: string]: InsightStore } = {};
const dataTypeStore: { [key: string]: DataType } = {};
const dataPagesStore: { [key: string]: DataPage } = {};
const genAICoachStore: { [key: string]: AIAgentCoachModel } = {};
const AIAgentStore: { [key: string]: AIAgentCoachModel } = {};
const languagePackStore: { [key: string]: LocaleUtils } = {};
const localizationServiceStore: { [key: string]: LocalizationService } = {};

/**
 * Provides APIs to update and retrieve metadata from the store
 * @module RulesStore
 */

const mergeNewRules = (
  rule: string,
  store: { [key: string]: StoreType },
  storeRuleModels: StoreType,
  ruleModels: StoreType
) => {
  const tempStoreModels = Array.isArray(storeRuleModels) ? storeRuleModels : [storeRuleModels];
  const tempRuleModels = Array.isArray(ruleModels) ? ruleModels : [ruleModels];

  tempRuleModels.forEach((newRule) => {
    const index = tempStoreModels.findIndex((storeRule) => storeRule.classID === newRule.classID);
    if (index === -1) {
      tempStoreModels.push(newRule);
    } else {
      const defaultProps = { isDeclarativeTarget: false };
      const newMergedRule = { ...tempStoreModels[index], ...defaultProps, ...newRule };
      tempStoreModels[index] = newMergedRule;
    }
  });

  store[rule] = tempStoreModels.length === 1 ? tempStoreModels[0] : tempStoreModels;
};

/**
 * [mergeStore]
 * Description : merges the store with the source rules based on the classID. If a rule with the same name and classID
 * exists it will be merged, else created.
 * @function mergeStore
 * @param rules   source rules
 * @param store   Existing store
 * @static
 * @private
 * @example <caption>Example for mergeStore.</caption>
 * PCore.getMetadataUtils().mergeStore({ apartment: { classID: "c-1" }}, {})
 * example for merging the apartment field rule into the store
 */
export const mergeStore = (rules: ResourceType = {}, store: { [key: string]: StoreType } = {}) => {
  // Blind copy to store if store is empty
  if (Object.keys(store).length === 0) {
    Utils.copy(rules, store);
    return;
  }

  Object.keys(rules).forEach((rule) => {
    const ruleModels = rules[rule];
    const storeRuleModels = store[rule];
    if (!storeRuleModels) {
      // store has no entry then set RuleModel
      store[rule] = ruleModels;
    } else {
      // merging new rules with the rules in the store
      mergeNewRules(rule, store, storeRuleModels, ruleModels);
    }
  });
};

/**
 * returns property metadata from store
 * @function getPropertyMetadata
 * @param {string|undefined} propertyName property name like 'FirstName' whose metadata must be obtained
 * @param {string} currentClassID (optional) class id to which property belongs to
 * @static
 * @returns property metadata as an object from store. If property name is not passed or property metadata
 * or store doesn't exists then null is returned
 * @example <caption>In this example, the API obtains the metadata of the apartment property under the c-1 class.</caption>
 * PCore.getMetadataUtils().getPropertyMetadata('apartment', 'c-1')
 * {
 *     classID: 'c-1',
 *     type: 'text'
 * }
 */
export const getPropertyMetadata = (
  propertyName: string | undefined,
  currentClassID: string = ''
): PropertyModel | PropertyModel[] | null => {
  if (!propertyName) {
    return null;
  }

  const propertyModels = propertyModelStore[propertyName];

  if (!propertyModels) {
    return null;
  }

  if (!currentClassID) {
    return Utils.deepClone(propertyModels);
  }

  let metadata;
  if (Array.isArray(propertyModels)) {
    metadata = propertyModels.find((modal) => modal.classID.toLowerCase() === currentClassID.toLowerCase()) || null;
  } else {
    metadata =
      propertyModels.classID.toLowerCase() === currentClassID.toLowerCase() ||
      ((propertyModels.type === 'Page' || propertyModels.type === 'Page List') &&
        propertyModels?.pageClass?.toLowerCase() === currentClassID.toLowerCase())
        ? propertyModels
        : null;
  }
  return metadata ? Utils.deepClone(metadata) : null;
};

export const getLocaleUtilsForLanguage = (locale: string) => {
  if (!languagePackStore[locale]) {
    languagePackStore[locale] = new LocaleUtils();
  }
  return languagePackStore[locale];
};

/**
 *
 * @param reference unique identifier of the a LocaleFields in Languagepack store
 * @param callback to be called to create LocalizationService for the given reference
 * @returns LocalizationService instance for the given reference
 *
 * @example <caption>In this example, the API obtains the LocalizationService for the reference "C-1!PAGE!VIEW1".</caption>
 * getLocalizationService("C-1!PAGE!VIEW1", () => {
 *  return new LocalizationService("C-1!PAGE!VIEW1");
 * });
 * returns LocalizationService instance for the given reference
 * @private
 */
export const getLocalizationServiceFromStore = (reference: string, callback: () => LocalizationService) => {
  if (!localizationServiceStore[reference]) {
    localizationServiceStore[reference] = callback();
  }
  return localizationServiceStore[reference];
};

/**
 * returns embedded property's leafNode metadata from store
 * @function getEmbeddedPropertyMetadata
 * @param {string} propertyName property name like 'Customer.Address.City' whose metadata must be obtained
 * @param {string} currentClassID class id to which top level property belongs to
 * @param {string} embeddedType embedded field type to which current leafNode belongs to
 * @param {string[]} categoryPath (optional) category to which the current leafNode belongs to.
 * @static
 * @returns embedded property's leafNode metadata as an object from store. If currentClassID is not passed
 * or property metadata or store doesn't exists then null is returned
 *
 * @example <caption>In this example, the API obtains the metadata of the City leaf node, which needs to be resolved from the c-1 top level class.</caption>
 *
 * //{Customer: [{ classID: 'c-1', pageClass: 'c-2', label:'Customer' }],
 * // Address: [{ classID: 'c-2', pageClass: 'c-3', label:'Address' }],
 * // City: [{ classID: 'c-3', label:'City' }]}
 *
 * PCore.getMetadataUtils().getEmbeddedPropertyMetadata('Customer.Address.City', 'c-1')
 * returns
 * {
 *    classID: 'c-3',
 *    category: 'Customer.Address'
 * }
 */

export const getEmbeddedPropertyMetadata = (
  propertyName: string,
  currentClassID?: string,
  embeddedType?: string,
  categoryPath: string[] = []
): object | null => {
  if (!currentClassID || !propertyName) {
    return null;
  }
  if (!propertyName.includes('.')) {
    let category;
    const metadata = getPropertyMetadata(propertyName, currentClassID);
    if (categoryPath.length) {
      category = categoryPath.join('.');
    }
    return {
      ...metadata,
      embeddedType,
      category
    };
  }
  const [first, ...rest] = propertyName.split('.');
  const metadata = getPropertyMetadata(first, currentClassID) as PropertyModel;
  if (metadata) {
    categoryPath.push(metadata.label as string);
  }
  return getEmbeddedPropertyMetadata(rest.join('.'), metadata?.pageClass, metadata?.type, categoryPath);
};

/**
 * This API updates the view store
 * @function updateViewStore
 * @param views the object of the views
 * @static
 * @private
 * @example <caption>Example for updateViewStore.</caption>
 * PCore.getMetadataUtils().updateViewStore({
 *  "view1": { a: 'a' }
 *});
 * example for updating view store with View1
 */
export const updateViewStore = (views: { [key: string]: ViewStore }) => {
  mergeStore(views, viewStore);
};

/**
 * This API updates the navigation
 * @function updateNavigationStore
 * @param navigations the object of navigation rules
 * @static
 * @private
 * @example <caption>Example for updateNavigationStore.</caption>
 * PCore.getMetadataUtils().updateNavigationStore({
 *      Navigation1: {
 *          nav: "nav"
 *      }
 *});
 * example for updating navigation store with Navigation1
 */
export const updateNavigationStore = (navigations: { [key: string]: NavigationStore }) => {
  mergeStore(navigations, navigationStore);
};

const mergeByResource = (resources: Resources) => {
  if (resources.views) mergeStore(resources.views, viewStore);
  if (resources.navigations) mergeStore(resources.navigations, navigationStore);
  if (resources.personalizations) mergeStore(resources.personalizations, personalizationStore);
  if (resources.insights) mergeStore(resources.insights, insightStore);
  if (resources.paragraphs) mergeStore(resources.paragraphs, paragraphStore);
  if (resources.dataTypes) mergeStore(resources.dataTypes, dataTypeStore);
  if (resources.datapages) mergeStore(resources.datapages, dataPagesStore);
  if (resources.genAICoaches) {
    mergeStore(resources.genAICoaches, genAICoachStore);
  }
  if (resources.AIAgents) {
    mergeStore(resources.AIAgents, AIAgentStore);
  }
};

const mergeFields = (resources: Resources) => {
  const fields = resources.fields || {};

  Object.keys(fields).forEach((key: string) => {
    const fieldArray = fields[key];

    if (Array.isArray(fieldArray)) {
      fieldArray.forEach((fieldObj) => {
        if (resources.datapages && fieldObj.datasource && fieldObj.datasource.tableType === 'DataPage') {
          fieldObj.datasource.mode = resources.datapages?.[fieldObj.datasource.name].mode;
        }
        // generate localeReference
        const localeReference = getLocaleReference(
          LocaleRuleTypes.FIELD,
          key,
          fieldObj.definedOnClassID ?? fieldObj.classID
        );
        fieldObj.localeReference = localeReference;
      });
      // if only single entry present for field then return object.
      if (fieldArray.length === 1) {
        const [objData] = fieldArray;
        fields[key] = objData;
      }
    } else if (resources.datapages && fieldArray?.datasource && fieldArray.datasource.tableType === 'DataPage') {
      // TODO: In future we will always get array once everything is stable remove this code.

      fieldArray.datasource.mode = resources.datapages[fieldArray.datasource.name].mode;
    }
  });
};

/**
 * Update store with given resources
 * @function updateStores
 * @param resources uiMetadata - meta data information
 * @static
 * @private
 * @example <caption>Example for updateStores.</caption>
 * PCore.getMetadataUtils().updateStores({
 *   resources: {
 *     personalizations: {
 *        "test1234": {}
 *  }
 *  }
 *});
 * example for updating store with personalizations
 */
export const updateStores = (resources: Resources) => {
  mergeByResource(resources);
  mergeFields(resources);

  const { datapages = {} } = resources;
  Object.keys(datapages).forEach((key) => {
    dataPageModelStore[key] = datapages[key] as unknown as DataPageModel;
  });

  mergeStore(resources.fields, propertyModelStore);
};

/**
 * returns view metadata
 *
 * @function resolveView
 * @param {String} name - view name whose metadata must be obtained
 * @static
 * @returns view metadata as an object
 *
 * @example <caption>In this example, the API obtains the metadata of the view View1.</caption>
 * PCore.getMetadataUtils().resolveView("View1");
 * returns
 * {
 *     "name": "View1",
 *     "type": "View",
 *     "config": {
 *         "type": "landingpage",
 *         "icon": "pi pi-home-solid",
 *         "title": "@ENV APPLICATION_DESC",
 *         "template": "WideNarrowPage",
 *         "ruleClass": "c-1",
 *         "localeReference": "@LR C-1!PAGE!VIEW1",
 *         "enableGetNextWork": false
 *     },
 *     "children": [
 *          {
 *             "name": "A",
 *             "type": "Region",
 *             "children": [
 *                 {
 *                    "type": "Pulse",
 *                     "config": {
 *                         "label": "@L Pulse",
 *                          "messageIDs": "@P pulse.messageIDs"
 *                       }
 *                 }
 *             ]
 *           }
 *     ],
 *     "classID": "c-1"
 * }
 */
export const resolveView = (name: string): object => {
  return viewStore[name];
};

/**
 * determines if view generated for a classID exists in store
 *
 * @function isViewExists
 * @param {string} viewName - view name whose existence needs to be confirmed
 * @param {string} classID - class ID for which view was generated
 * @static
 * @returns {boolean} true if view generated for a classID exists in store
 *
 * @example <caption>In this example, the API returns the Boolean value true if the view View1 generated for the class O02LDN-CosmoReact-Work-Test exists in the Store.</caption>
 * PCore.getMetadataUtils().isViewExists("View1", "O02LDN-CosmoReact-Work-Test");
 */
export const isViewExists = (viewName: string, classID: string) => {
  const view = viewStore[viewName];
  if (Array.isArray(view)) {
    return view.some((prop) => prop.classID === classID);
  }
  return view?.classID === classID;
};

/**
 * Will provide navigation object
 *
 * @function resolveNavigation
 * @param name - navigation name
 * @static
 * @returns returns navigation rule
 * @private
 * @example <caption>Example for resolveNavigation.</caption>
 * PCore.getMetadataUtils().resolveNavigation("Navigation1");
 * example for getting "Navigation1" navigation rule object
 */
export const resolveNavigation = (name: string): object => {
  return navigationStore[name];
};

/**
 * returns metadata for a dataPage
 *
 * @function getDataPageMetadata
 * @param {string} dataPageName name of the dataPage whose metadata must be obtained
 * @static
 * @returns dataPage metadata as an object if present. If dataPage doesn't exists null will be returned
 *
 * @example <caption>In this example, the API obtains the metadata of the D_pyMyWorkList data page.</caption>
 * PCore.getMetadataUtils().getDataPageMetadata("D_pyMyWorkList");
 * returns
 *   {
 *      "classID":"Assign-Worklist",
 *      "mode":"readonly",
 *       "isSearchable":false,
 *       "isQueryable":true,
 *       "structure":"list",
 *       "refreshStrategy":{
 *          "type":"reloadOncePerInteraction"
 *       },
 *       "isWorkObject":false,
 *       "isAssignObject":true
 *    }
 */
export const getDataPageMetadata = (dataPageName: string): DataPageModel | null => {
  return dataPageName in dataPageModelStore ? dataPageModelStore[dataPageName] : null;
};

/**
 * returns metadata of personalization rule
 *
 * @function getPersonalizationMetadata
 * @param {string} personalizationId personalization id whose metadata must be obtained
 * @static
 * @returns personalization metadata as an object if present. If personalizationId doesn't exists null will be returned
 *
 * @example <caption>In this example, the API obtains the metadata of the PersonalizationSample personalization.</caption>
 * PCore.getMetadataUtils().getPersonalizationMetadata("PersonalizationSample");
 * returns
 *    {
 *       "defaultPersonalization":"",
 *       "allPersonalizations": []
 *    }
 */

export const getPersonalizationMetadata = (personalizationId: string): PersonalizationStore | null => {
  return personalizationId in personalizationStore ? personalizationStore[personalizationId] : null;
};

/**
 * returns metadata of insight rule
 *
 * @function getInsight
 * @param insightId insight id whose metadata must be obtained
 * @static
 * @returns insight metadata as an object if present. If insight is not present in store then null will be returned
 *
 * @example <caption>In this example, the API obtains the metadata of the InsightId insight.</caption>
 * PCore.getMetadataUtils().getInsight("InsightId");
 * returns
 *     {
 *       "pyID":"InsightId",
 *       "pyContent":"{}",
 *       "pyClassLabel":"Test"
 *    }
 */
export const getInsight = (insightId: string): InsightStore | null => {
  return insightId in insightStore ? insightStore[insightId] : null;
};

/**
 * This API returns fields of given class name
 *
 * @function getFieldsByClassName
 * @param className name of the class
 * @static
 * @returns object of the fields for the given class name
 * @private
 * @example <caption>Example for getFieldsByClassName.</caption>
 * PCore.getMetadataUtils().getFieldsByClassName("O02LDN-CosmoReact-Work-Test ");
 * example for getting fields of the "O02LDN-CosmoReact-Work-Test" class
 */
export const getFieldsByClassName = (className: string): { [key: string]: Field } => {
  const fields: { [key: string]: Field } = {};
  Object.keys(propertyModelStore).forEach((fieldName) => {
    const props = propertyModelStore[fieldName];
    let matchedField;
    if (Array.isArray(props)) {
      matchedField = props.find((prop) => className === prop.classID);
    } else if (className === props.classID) {
      matchedField = props;
    }
    if (matchedField) {
      fields[fieldName] = Utils.deepClone(matchedField);
    }
  });
  return fields;
};

/**
 * check if a property has a datasource and returns its parameters
 *
 * @function getFieldParameters
 * @param {string} propertyName name of the property whose parameters must be obtained
 * @param {string} classID classID of the property
 * @static
 * @returns property parameters as an object. If property data source doesn't has parameters or property is not associated to datasource
 * null be returned
 * @example <caption>In this example, the API obtains the parameters of the property TestProperty that belongs to the class c-1.</caption>
 * PCore.getMetadataUtils().getFieldParameters("TestProperty", "c-1");
 * returns
 *     {
 *        "param1":"test"
 *     }
 */
export const getFieldParameters = (propertyName: string, classID: string) => {
  const meta = getPropertyMetadata(propertyName, classID) as PropertyModel;
  const params = meta?.datasource?.parameters;
  if (params) {
    return Utils.deepClone(params);
  }

  return null;
};

/**
 * This API returns paragraph of given class name
 *
 * @function getParagraph
 * @param className name of the paragraph
 * @static
 * @returns object of the paragraph for the given class name
 * @private
 * @example <caption>Example for getParagraph.</caption>
 * PCore.getMetadataUtils().getParagraph("testPara", " O02LDN-CosmoReact-Work-Test ");
 * example for getting paragraph of the "O02LDN-CosmoReact-Work-Test" class
 */
export const getParagraph = (paragraphName: string, classID: string) => {
  let value: ParagraphStore | null = null;
  const paragraphObj = paragraphStore[paragraphName];

  if (paragraphObj) {
    if (Array.isArray(paragraphObj)) {
      value = paragraphObj.find((paragraph) => paragraph.classID === classID);
    } else if (paragraphObj.classID === classID) {
      value = paragraphObj;
    }
  }

  return value ? Utils.deepClone(value) : null;
};

/**
 * returns GenAI coach metadata
 *
 * @function getGenAICoach
 * @param {string} genAICoachName GenAI Coach Name whose metadata must be obtained
 * @param {string} classID GenAI Class ID
 * @static
 * @returns GenAI Coach metadata as an object. if GenAI coach name doesn't exists in store
 * or GenAI coach name doesn't associate with the provided class null will be returned
 *
 * @example <caption>In this example, the API obtains the metadata of the pyGenAICoachDefault GenAI coach associated with the Work- class.</caption>
 * PCore.getMetadataUtils().getGenAICoach("pyGenAICoachDefault", "Work-");
 */
export const getGenAICoach = (genAICoachName: string, classID: string): AIAgentCoachModel | null => {
  let value: AIAgentCoachModel | null = null;
  const genAICoachObj = genAICoachStore[genAICoachName];

  if (genAICoachObj) {
    if (Array.isArray(genAICoachObj)) {
      value = genAICoachObj.find((genAICoach) => genAICoach.classID === classID);
    } else if (genAICoachObj.classID === classID) {
      value = genAICoachObj;
    } else {
      // Leave as null
    }
  }
  return value ? Utils.deepClone(value) : null;
};

/**
 * returns AI Agent metadata
 *
 * @function getAIAgent
 * @param {string} AIAgentName AI Agent Name whose metadata must be obtained
 * @param {string} classID GenAI Class ID
 * @static
 * @returns AI Agent metadata as an object. if AI Agent name doesn't exists in store
 * or AI Agent name doesn't associate with the provided class null will be returned
 *
 * @example <caption>In this example, the API obtains the metadata of the pyAIAgentDefault AI Agent associated with the Work- class.</caption>
 * PCore.getMetadataUtils().getAIAgent("pyAIAgentDefault", "Work-");
 */
export const getAIAgent = (AIAgentName: string, classID: string): AIAgentCoachModel | null => {
  let value = null;
  const AIAgentObj = AIAgentStore[AIAgentName];

  if (AIAgentObj) {
    if (Array.isArray(AIAgentObj)) {
      value = AIAgentObj.find((AIAgent) => AIAgent.classID === classID);
    } else if (AIAgentObj.classID === classID) {
      value = AIAgentObj;
    } else {
      // Leave as null
    }
  }
  return value ? Utils.deepClone(value) : null;
};

/**
 * returns Savable data page name for data class
 *
 * @function getSavableDataPage
 * @param {string} dataClass unique identifier of the dataClass whose savable data page name must be obtained
 * @static
 * @returns Savable data page name as an string. if Savable data page doesn't exists in store null will be returned
 * @private
 * @example <caption>example for getting savable data page of data class "CarMake" class</caption>
 * PCore.getMetadataUtils().getSavableDataPage("CarMake");
 * returns
 * D_CarMakeSavable
 */
export const getSavableDataPage = (dataClass: string) => {
  if (dataClass in dataTypeStore) {
    const { savableDataPage } = dataTypeStore[dataClass];
    return savableDataPage;
  }
  return null;
};

/**
 * returns keys of the data page
 *
 * @function getDataPageKeys
 * @param {string} dataPageName data page name whose keys must be obtained
 * @static
 * @returns keys as an array of objects containing (linkedField, keyName and isAlternateKeyStorage). If data page doesn't exists null will be returned or
 * if data page class doesn't exists in store it will return empty
 * @private
 * @example <caption>example of getting data page keys for "TestDataPage"</caption>
 * PCore.getMetadataUtils().getDataPageKeys("TestDataPage");
 * returns
 *    [{
 *       "linkedField":"RegNo",
 *       "keyName":"pyGUID",
 *       "isAlternateKeyStorage":true
 *    }]
 */
export const getDataPageKeys = (dataPageName: string): DataPageObj[] | null => {
  const dataPage = dataPagesStore[dataPageName];
  if (!dataPage) {
    return null;
  }

  const { classID: dataClass, isAlternateKeyStorage, parameters } = dataPage;

  if (isAlternateKeyStorage) {
    return parameters.map((item) => ({
      keyName: item.name,
      isAlternateKeyStorage,
      linkedField: item.linkedField
    }));
  }

  const classData = dataTypeStore[dataClass];
  if (!classData) {
    return [];
  }

  return classData.classKeys.map((keyName: string) => ({
    keyName,
    isAlternateKeyStorage
  }));
};

/**
 * returns lookup datapage name
 *
 * @function getLookUpDataPage
 * @param {string} dataClass unique identifier of data class whose lookup page name must be obtained
 * @static
 * @returns lookup data page name as an string. if lookup data page  doesn't exists in store  null will be returned
 * @private
 * @example <caption>example of getting getLookUpDataPage for "c-1" classID</caption>
 * PCore.getMetadataUtils().getLookUpDataPage("c-1");
 * returns
 * D_Registration
 */
export const getLookUpDataPage = (dataClass: string) => {
  if (dataClass in dataTypeStore) {
    const { lookupDataPage } = dataTypeStore[dataClass];
    return lookupDataPage;
  }
  return null;
};

/**
 * returns lookup datapage info of a data class
 *
 * @function getLookUpDataPageInfo
 * @param {string} dataClass unique identifier of a data class whose lookup data page info must be obtained
 * @static
 * @returns lookup data page info as an object. if lookup data page  doesn't exists in store  null will be returned
 * @private
 * @example <caption>example of getting getLookUpDataPageInfo for "Registration" classID</caption>
 * // data type
 * //  {
 * //     "Registration":{
 * //        "savableDataPage":"D_RegistrationSavable",
 * //        "lookupDataPage":"D_Registration",
 * //        "classKeys":[
 * //           "pyGUID"
 * //         ],
 * //        "lookupDataPageInfo":{
 * //           "isAlternateKeyStorage":true,
 * //          "parameters":{
 * //              "TestID":"@P .TestID"
 * //           }
 * //        }
 * //     }
 * //  }
 * PCore.getMetadataUtils().getLookUpDataPageInfo("Registration");
 * returns
 *    {
 *       "isAlternateKeyStorage":true,
 *       "parameters":{
 *          "TestID":"@P .TestID"
 *       }
 *    }
 */
export const getLookUpDataPageInfo = (dataClass: string) => {
  if (Object.hasOwn(dataTypeStore, dataClass)) {
    const { lookupDataPageInfo } = dataTypeStore[dataClass];
    return lookupDataPageInfo;
  }
  return null;
};

/**
 *
 * @param propertyReference
 * @param context
 * @private
 */
export const getAdditionalInfo = (propertyReference: string, context: string) => {
  const pageRef = propertyReference.split('.');
  const propName = pageRef.pop();
  const c11nEnv = new C11nEnv({
    options: {
      pageReference: `${pageRef.join('.')}`,
      context
    }
  });

  const additionalInfo = (getPropertyMetadata(propName, c11nEnv.getCurrentClassID()) as PropertyModel)
    ?.additionalInformation;

  if (!additionalInfo?.includes('@PARAGRAPH')) {
    return additionalInfo;
  }

  const paragraphRuleName = AnnotationProcessor.trimAnnotation(additionalInfo);

  return ParagraphUtils.processParagraph(paragraphRuleName, c11nEnv);
};

/**
 * returns the resolved fieldMetadata of the given property
 * @param {string} propertyReference The complete reference of the property whose metadata has to be obtained
 * @param {string} context The context in which the prop is present
 * @returns resolved fieldMetadata as an object
 * @example <caption>In this example, the API obtains the resolved field metadata of the FirstName property under the app/primary context.</caption>
 * //fieldMetadata
 * //   {
 * //      "FirstName":[
 * //         {
 * //            "classID":"Address",
 * //            "type":"text",
 * //            "maxLength":10,
 * //            "additionalInformation":"@PARAGRAPH Instruction"
 * //         }
 * //      ]
 * //   }
 * const propertyRef = "caseInfo.content.FirstName";
 * const context = "app/primary";
 * const resolvedFieldMetadata = PCore.getMetadataUtils().getResolvedFieldMetadata(propertyReference, context);
 * returns
 *    {
 *       "classID":"Address",
 *       "type":"text",
 *       "maxLength":10,
 *       "additionalInformation":"<p>Hello, how are you</p>"
 *    }
 */
export const getResolvedFieldMetadata = (propertyReference: string, context: string) => {
  const pageRef = propertyReference.split('.');
  const propName = pageRef.pop();

  const c11nEnv = new C11nEnv({
    options: {
      pageReference: `${pageRef.join('.')}`,
      context
    }
  });

  const fieldMetadata = getPropertyMetadata(propName, c11nEnv.getCurrentClassID());
  const resolvedFieldMetadata = {};
  c11nEnv._classID = c11nEnv.getCurrentClassID();
  c11nEnv.resolveConfigProps(fieldMetadata, resolvedFieldMetadata);
  return resolvedFieldMetadata;
};
